import React, { useEffect, useState, useMemo } from "react";

import { useQuery } from "react-query";
import { useAlert } from "react-alert";

import { debounce } from "lodash";

import {
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Table,
  TableBody,
  TableContainer,
  Typography,
  Button,
} from "@material-ui/core";

import { Search } from "@material-ui/icons";

import Navigation from "../Navigation";

import {
  GetAccessReponse,
  AccountAccess,
  EntitlementsByCategory,
  AccessByCategory,
} from "./types";
import { CategoryAccess } from "./EntitlementCategory";

import { makeRequest } from "../util";

const useFetchAccountAccess = () => {
  return useQuery("fetchAccountAccess", async () => {
    const { data: res } = await makeRequest<GetAccessReponse>(
      "get_account_access"
    );
    return res;
  });
};

export const Entitlements = () => {
  const [accessByCategory, setAccessByCategory] = useState<AccessByCategory[]>(
    []
  );
  const [needSave, setNeedSave] = useState<boolean>(false);

  const [searchString, setSearchString] = useState<string>("");
  const [searchStringDisplay, setSearchStringDisplay] = useState<string>("");

  const { data, isLoading, refetch } = useFetchAccountAccess();
  const alert = useAlert();

  const accountAccess: AccountAccess[] = useMemo(() => {
    return data?.account_access ?? [];
  }, [data]);

  const entitlementsByCategory: EntitlementsByCategory[] = useMemo(() => {
    return data?.entitlements_by_category ?? [];
  }, [data]);

  const categories: Map<string, AccessByCategory> = useMemo(() => {
    const map = new Map(
      entitlementsByCategory.map((item) => [
        item.id,
        {
          id: item.id,
          category: item.category,
          has_category_access: false,
          entitlements: [],
        },
      ])
    );
    return map;
  }, [entitlementsByCategory]);

  useEffect(() => {
    const categoriesWithAccess: AccessByCategory[] = Array.from(
      categories.values()
    ).map((c) => {
      const entitlements = entitlementsByCategory.filter(
        (ic) => ic.id === c.id
      );
      return {
        id: c.id,
        category: c.category,
        has_category_access: !!accountAccess.find(
          (a) => a.entitlement_category_id === c.id
        ),
        entitlements: entitlements.map((ic) => ({
          ...ic,
          has_access: !!accountAccess.find(
            (a) => a.entitlement_id === ic.entitlement_id
          ),
          entitlement_search_target: `${ic.entitlement_name}`
            .trim()
            .toLowerCase(),
        })),
      };
    });
    setAccessByCategory(categoriesWithAccess);
  }, [accountAccess, entitlementsByCategory, categories]);

  const filteredAccessByCategory = useMemo(() => {
    if (!accessByCategory) {
      return [];
    }
    if (searchString === "") {
      return accessByCategory;
    }

    const filtered = accessByCategory.map((d) => {
      const filteredEntitlements = d.entitlements.filter((i) => {
        const searchTarget = i.entitlement_search_target;
        return searchTarget.includes(searchString);
      });
      return { ...d, entitlements: filteredEntitlements };
    });

    return filtered.filter((d) => d.entitlements.length > 0);
  }, [accessByCategory, searchString]);

  const onSearch = debounce((searchString: string) => {
    setSearchString(searchString.trim().toLowerCase());
  }, 500);

  const onSearchDisplay = (searchString: string) => {
    setSearchStringDisplay(searchString);
    onSearch(searchString);
  };

  useEffect(() => {
    return onSearch.cancel();
  }, [onSearch]);

  const handleEntitlementAccessChange = (
    changedEntitlementId: string,
    checked: boolean
  ) => {
    const category = accessByCategory.find((a) =>
      a.entitlements.find((i) => i.entitlement_id === changedEntitlementId)
    );
    setAccessByCategory((prevState) =>
      prevState.map((a) => {
        return {
          ...a,
          has_category_access:
            category?.id === a.id ? checked : a.has_category_access,
          entitlements: a.entitlements.map((i) => {
            if (i.entitlement_id === changedEntitlementId) {
              return { ...i, has_access: checked };
            }
            return i;
          }),
        };
      })
    );
    setNeedSave(true);
  };

  const saveAccess = async () => {
    const data = accessByCategory.flatMap((a) =>
      a.entitlements.map((i) => ({
        entitlement_id: i.entitlement_id,
        disabled: !i.has_access,
      }))
    );

    makeRequest("save_account_access", data)
      .then(() => {
        setNeedSave(false);
        refetch();
      })
      .catch((error) => {
        alert.error(
          "Something went wrong. Unable to make change to destination access."
        );
      });
  };

  return (
    <Navigation>
      {isLoading ? (
        <div>...</div>
      ) : (
        <div className="w-full p-10">
          <Grid container className="my-4">
            <Grid item xs={9}>
              <Typography variant="h4">Destination Access</Typography>
            </Grid>
            <Grid item xs={3} className="flex items-center">
              <Search />
              <TextField
                fullWidth
                placeholder="Search..."
                type="search"
                value={searchStringDisplay}
                variant="standard"
                onChange={(e) => onSearchDisplay(e.target.value)}
              />
            </Grid>
          </Grid>
          <Card className="outline-none">
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {filteredAccessByCategory.map((ca) => (
                    <CategoryAccess
                      key={`destination-access-row-${ca.id}-search-${searchString}`}
                      expand={searchString !== ""}
                      category={ca}
                      onEntitlementAccessChange={handleEntitlementAccessChange}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {needSave && (
              <CardContent className="flex justify-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={saveAccess}
                >
                  Save
                </Button>
              </CardContent>
            )}
          </Card>
        </div>
      )}
    </Navigation>
  );
};
